/* eslint-disable react/jsx-props-no-spreading */
import clsx from 'clsx';
import React, { forwardRef, useCallback } from 'react';

import mergeClasses from '../../../utils/shallowMerge';
import defaultClasses from './Button.module.scss';
import { Link } from '../link/Link';
import { detectBrowser, isMobileDevice } from '../../../utils/detectBrowser';

import { ButtonIcon } from './ButtonIcon';
import { ButtonLabel } from './ButtonLabel';

const Button = forwardRef((props, ref) => {
  const {
    as = 'button',
    children = [],
    ariaLabel = '',
    ariaControls = '',
    ariaExpanded = false,
    ariaLabelledby = '',
    ariaCurrent = 'false',
    tabIndex = '0',
    className = '',
    submit = false,
    disabled = false,
    variant : defaultVariant,
    onClick = () => null,
    customClasses,
    href = '',
    external,
    nodeLink,
    disableTouchEvent = false,
    hiddenText,
    icon,
    iconPosition = 'left',
    isInverted,
    ...attrs
  } = props;
  const styles = mergeClasses(defaultClasses, customClasses);
  const browser = detectBrowser();
  const Element = href ? 'a' : 'button';
  const buttonType = submit ? 'submit' : 'button';
  const variant = defaultVariant || 'primary';

  const checkType = () =>
    (variant.toLowerCase().includes('outlined') && 'outlined') || (variant.includes('link') && 'link') || 'normal';

  const checkInverted = () => (variant.includes('Inversed') ? 'inverted' : '');
  const mainVariant = variant.toLowerCase().includes('primary')
    ? 'primary'
    : variant.toLowerCase().includes('secondary')
      ? 'secondary'
      : variant;

  const customInnerClasses = clsx(
    styles.button,
    styles[mainVariant],
    styles[`variant__${as}`],
    {
      [` ${styles[checkType()]}`]: as !== 'text' || variant.includes('link'),
      [styles.disabled]: disabled,
    },
    styles[checkInverted()],
    className,
  );

  const handleOnClick = useCallback(
    (event) => {
      if (disabled) {
        event.preventDefault();
        return;
      }
      onClick && onClick(event);
    },
    [onClick, disabled],
  );

  const onClickAction =
    isMobileDevice && !disableTouchEvent && browser !== 'Safari'
      ? {
          onClick: (e) => handleOnClick(e),
          onTouchEnd: (e) => handleOnClick(e),
        }
      : { onClick: (e) => handleOnClick(e) };

  return nodeLink && nodeLink.type ? (
    <Link
      className={customInnerClasses}
      dataButtonType="nodeLink"
      link={{ reference: nodeLink }}
      onClick={(e) => handleOnClick(e)}
      ariaLabel={ariaLabel}
      disabled={disabled}
    >
      {icon && iconPosition == 'left' && <div className={clsx(styles.icon, styles.icon_left)}>{icon}</div>}
      <span aria-hidden="true">{children}</span>
      {icon && iconPosition == 'right' && <div className={clsx(styles.icon, styles.icon_right)}>{icon}</div>}
      <span className={styles.visuallyHidden}>{hiddenText}</span>
    </Link>
  ) : (
    <Element
      ref={ref}
      type={buttonType}
      aria-controls={ariaControls}
      aria-current={ariaCurrent}
      aria-expanded={ariaExpanded}
      aria-label={ariaLabel}
      aria-labelledby={ariaLabelledby}
      className={customInnerClasses}
      disabled={disabled}
      href={!disabled && href ? href : null}
      target={!disabled && href && external ? '_blank' : undefined}
      tabIndex={tabIndex}
      role={Element === 'button' ? Element : 'link'}
      {...onClickAction}
      {...attrs}
    >
      <span className={styles.label}>{children}</span>
    </Element>
  );
});


Button.displayName = 'Button';
Button.Icon = ButtonIcon;
Button.Label = ButtonLabel;

export default Button;
